(function () {
  var instances = new WeakMap();
  var instancesBySectionId = {};
  var youtubeApiPromise = null;
  var vimeoApiPromise = null;

  var getThemeStrings = function () {
    return (window.theme && window.theme.strings && window.theme.strings.accessibility) || {};
  };

  var getThemeIcons = function () {
    return (window.theme && window.theme.icons) || {};
  };

  var isSectionAlive = function (section, initToken) {
    return Boolean(initToken && initToken.alive && section && section.isConnected);
  };

  var clearExternalVideoContainer = function (container) {
    if (!container) {
      return;
    }

    container.innerHTML = '';
  };

  var loadYouTubeAPI = function () {
    if (youtubeApiPromise) {
      return youtubeApiPromise;
    }

    if (window.YT && window.YT.loaded) {
      youtubeApiPromise = Promise.resolve();
      return youtubeApiPromise;
    }

    youtubeApiPromise = new Promise(function (resolve) {
      var previousCallback = window.onYouTubeIframeAPIReady;

      window.onYouTubeIframeAPIReady = function () {
        if (typeof previousCallback === 'function') {
          previousCallback();
        }

        resolve();
      };

      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        var tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
      }
    });

    return youtubeApiPromise;
  };

  var loadVimeoAPI = function () {
    if (vimeoApiPromise) {
      return vimeoApiPromise;
    }

    if (window.Vimeo) {
      vimeoApiPromise = Promise.resolve();
      return vimeoApiPromise;
    }

    vimeoApiPromise = new Promise(function (resolve) {
      var tag = document.createElement('script');
      tag.src = 'https://player.vimeo.com/api/player.js';
      tag.onload = resolve;
      document.body.appendChild(tag);
    });

    return vimeoApiPromise;
  };

  var setPauseButtonState = function (button, isPlaying) {
    if (!button) {
      return;
    }

    var strings = getThemeStrings();
    var icons = getThemeIcons();
    var useIcon = button.dataset.pauseButtonContent === 'icon';

    button.setAttribute('aria-pressed', isPlaying ? 'false' : 'true');
    button.setAttribute(
      'aria-label',
      isPlaying
        ? strings.pause_video || 'Pause'
        : strings.play_video || 'Play'
    );

    if (useIcon) {
      button.innerHTML = isPlaying
        ? icons.pause || button.innerHTML
        : icons.play || button.innerHTML;
      return;
    }

    button.innerText = isPlaying
      ? strings.pause_video || 'Pause'
      : strings.play_video || 'Play';
  };

  var initYouTubeVideo = function (section, container, videoId, initToken, registerPlayer) {
    clearExternalVideoContainer(container);

    return loadYouTubeAPI().then(function () {
      if (!isSectionAlive(section, initToken)) {
        return null;
      }

      return new Promise(function (resolve) {
        var player = new window.YT.Player(container, {
          videoId: videoId,
          playerVars: {
            autoplay: 1,
            controls: 0,
            mute: 1,
            loop: 1,
            playlist: videoId,
            playsinline: 1,
            modestbranding: 1,
            rel: 0,
            iv_load_policy: 3,
            disablekb: 1,
          },
          events: {
            onReady: function () {
              if (!isSectionAlive(section, initToken)) {
                try {
                  player.destroy();
                } catch (error) {}

                resolve(null);
                return;
              }

              resolve({
                play: function () {
                  player.playVideo();
                },
                pause: function () {
                  player.pauseVideo();
                },
                isPlaying: function () {
                  return player.getPlayerState() === window.YT.PlayerState.PLAYING;
                },
                destroy: function () {
                  try {
                    player.destroy();
                  } catch (error) {}

                  clearExternalVideoContainer(container);
                },
              });
            },
          },
        });

        if (typeof registerPlayer === 'function') {
          registerPlayer(player);
        }
      });
    });
  };

  var initVimeoVideo = function (section, container, videoId, initToken, registerPlayer) {
    clearExternalVideoContainer(container);

    return loadVimeoAPI().then(function () {
      if (!isSectionAlive(section, initToken)) {
        return null;
      }

      var player = new window.Vimeo.Player(container, {
        id: videoId,
        autoplay: true,
        loop: true,
        muted: true,
        controls: false,
        background: true,
      });

      if (typeof registerPlayer === 'function') {
        registerPlayer(player);
      }

      if (!isSectionAlive(section, initToken)) {
        player.destroy().finally(function () {
          clearExternalVideoContainer(container);
        });
        return null;
      }

      return {
        play: function () {
          player.play();
        },
        pause: function () {
          player.pause();
        },
        isPlaying: function () {
          return player.getPaused().then(function (paused) {
            return !paused;
          });
        },
        destroy: function () {
          player.destroy().finally(function () {
            clearExternalVideoContainer(container);
          });
        },
      };
    });
  };

  var initExternalVideo = function (section, container, provider, videoId, initToken, registerPlayer) {
    if (provider === 'youtube') {
      return initYouTubeVideo(section, container, videoId, initToken, registerPlayer);
    }

    if (provider === 'vimeo') {
      return initVimeoVideo(section, container, videoId, initToken, registerPlayer);
    }

    return Promise.resolve(null);
  };

  var ensureExternalTarget = function (playerWrapper) {
    if (!playerWrapper) {
      return null;
    }

    var target = playerWrapper.querySelector('[data-video-hero-external]');

    if (!target) {
      target = document.createElement('div');
      target.setAttribute('data-video-hero-external', '');
      playerWrapper.appendChild(target);
    }

    return target;
  };

  var init = function (section) {
    var sectionId = section.dataset.sectionId;
    var initToken = { alive: true };
    var externalPlayer = section.querySelector('.video-hero__external-player');
    var pauseButton = section.querySelector('[data-video-hero-pause]');
    var controllerPromise = Promise.resolve(null);
    var activeController = null;
    var pendingPlayer = null;
    var listeners = [];
    var destroyed = false;

    if (!externalPlayer) {
      return null;
    }

    var provider = externalPlayer.dataset.videoProvider;
    var videoId = externalPlayer.dataset.videoId;
    var externalVideo = ensureExternalTarget(externalPlayer);

    if (!externalVideo || !provider || !videoId) {
      return null;
    }

    if (sectionId && instancesBySectionId[sectionId]) {
      instancesBySectionId[sectionId].destroy();
    }

    var registerPlayer = function (player) {
      pendingPlayer = player;
    };

    var destroyController = function (controller) {
      if (!controller || typeof controller.destroy !== 'function') {
        return;
      }

      controller.destroy();
    };

    var clearPlayer = function () {
      clearExternalVideoContainer(externalPlayer);
      ensureExternalTarget(externalPlayer);
    };

    controllerPromise = initExternalVideo(
      section,
      externalVideo,
      provider,
      videoId,
      initToken,
      registerPlayer
    ).then(function (controller) {
      if (destroyed) {
        destroyController(controller);
        return null;
      }

      activeController = controller;
      pendingPlayer = null;
      setPauseButtonState(pauseButton, true);
      return controller;
    });

    if (pauseButton) {
      var onPause = function () {
        controllerPromise.then(function (controller) {
          if (!controller || typeof controller.isPlaying !== 'function') {
            return;
          }

          var applyToggle = function (isPlaying) {
            if (isPlaying) {
              controller.pause();
              setPauseButtonState(pauseButton, false);
              return;
            }

            controller.play();
            setPauseButtonState(pauseButton, true);
          };

          var playingState = controller.isPlaying();

          if (playingState && typeof playingState.then === 'function') {
            playingState.then(applyToggle);
            return;
          }

          applyToggle(playingState);
        });
      };

      pauseButton.addEventListener('click', onPause);
      listeners.push(function () {
        pauseButton.removeEventListener('click', onPause);
      });
    }

    var instance = {
      destroy: function () {
        if (destroyed) {
          return;
        }

        destroyed = true;
        initToken.alive = false;

        destroyController(activeController);
        activeController = null;

        controllerPromise.then(function (controller) {
          if (controller && controller !== activeController) {
            destroyController(controller);
          }
        });

        if (pendingPlayer) {
          try {
            if (typeof pendingPlayer.destroy === 'function') {
              pendingPlayer.destroy();
            }
          } catch (error) {}

          pendingPlayer = null;
        }

        clearPlayer();

        listeners.forEach(function (unsubscribe) {
          unsubscribe();
        });

        instances.delete(section);

        if (sectionId && instancesBySectionId[sectionId] === instance) {
          delete instancesBySectionId[sectionId];
        }
      },
    };

    if (sectionId) {
      instancesBySectionId[sectionId] = instance;
    }

    instances.set(section, instance);

    return instance;
  };

  var destroy = function (section) {
    var instance = instances.get(section);

    if (instance) {
      instance.destroy();
      return;
    }

    var sectionId = section && section.dataset ? section.dataset.sectionId : null;

    if (sectionId && instancesBySectionId[sectionId]) {
      instancesBySectionId[sectionId].destroy();
    }
  };

  var destroyBySectionId = function (sectionId) {
    if (!sectionId || !instancesBySectionId[sectionId]) {
      return;
    }

    instancesBySectionId[sectionId].destroy();
  };

  var initAll = function (scope) {
    var root = scope || document;

    root.querySelectorAll('[data-video-hero-external-root]').forEach(function (section) {
      destroy(section);
      init(section);
    });
  };

  var destroyAll = function (scope) {
    var root = scope || document;

    root.querySelectorAll('[data-video-hero-external-root]').forEach(function (section) {
      destroy(section);
    });
  };

  var containsExternalVideoHero = function (scope) {
    if (!scope) {
      return false;
    }

    if (scope.matches && scope.matches('[data-video-hero-external-root]')) {
      return true;
    }

    return Boolean(scope.querySelector('[data-video-hero-external-root]'));
  };

  var handleSectionLoad = function (event) {
    var scope = event.target;

    if (!containsExternalVideoHero(scope)) {
      return;
    }

    if (event.detail && event.detail.sectionId) {
      destroyBySectionId(String(event.detail.sectionId));
    }

    initAll(scope);
  };

  var handleSectionUnload = function (event) {
    var scope = event.target;

    if (!containsExternalVideoHero(scope)) {
      return;
    }

    destroyAll(scope);

    if (event.detail && event.detail.sectionId) {
      destroyBySectionId(String(event.detail.sectionId));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  document.addEventListener('shopify:section:reorder', function (event) {
    if (!containsExternalVideoHero(event.target)) {
      return;
    }

    initAll(event.target);
  });
})();
